import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { AppLayout, PageBackLink } from '../../../components/layout';
import { ExpenseLineRow } from '../components';
import { INITIAL_EXPENSE_LINE_STATE } from '../constants/otrFormState';
import {
  getTravelRequestById,
  getExpensePoliciesByTier,
  submitTravelExpenses,
} from '../../../services/outstationTravelService';
import sessionDB from '../../../utils/sessionDB';
import { getEligibleExpenseHeads } from '../utils/companyArranged';

export const OTRExpenseSubmissionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [policyHeads, setPolicyHeads] = useState([]);
  const [expenseLines, setExpenseLines] = useState([INITIAL_EXPENSE_LINE_STATE]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const currentUserId = sessionDB.getEmpId();

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getTravelRequestById(id);
        setRequest(data);
        const policies = await getExpensePoliciesByTier(data.tenantId, data.tierId);
        setPolicyHeads(policies);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const addLine = () => {
    setExpenseLines([...expenseLines, { ...INITIAL_EXPENSE_LINE_STATE }]);
  };

  const removeLine = (index) => {
    setExpenseLines(expenseLines.filter((_, i) => i !== index));
  };

  const updateLine = (index, newLine) => {
    const updated = [...expenseLines];
    updated[index] = newLine;
    setExpenseLines(updated);
  };

  // Heads the company arranges for this request are not claimable by the employee.
  const eligibleHeads = getEligibleExpenseHeads(policyHeads, request?.companyArranged);

  const canSubmitExpenses = request &&
    Number(request.empId) === Number(currentUserId) &&
    request.status === 'Request_Approved';

  const handleSubmit = async () => {
    if (!canSubmitExpenses || expenseLines.length === 0) return;

    const incompleteLine = expenseLines.find(
      (line) => !line.expenseHeadId || !line.entryDate || !line.amount
    );
    if (incompleteLine) {
      alert('Complete the expense head, date and amount for every line.');
      return;
    }

    const eligibleIds = new Set(eligibleHeads.map(h => Number(h.headId)));
    if (expenseLines.some(line => !eligibleIds.has(Number(line.expenseHeadId)))) {
      alert('One or more lines use an expense head that is arranged by the company. Remove or change them.');
      return;
    }

    setSubmitting(true);
    try {
      // The service saves all lines and then submits the whole claim
      // through the contract-defined submit-expenses endpoint.
      await submitTravelExpenses(id, expenseLines);

      navigate(`/outstation-travel/${id}`);
    } catch (err) {
      console.error('Failed to submit all expense lines:', err);
      alert(err.message || 'Failed to submit expenses.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <AppLayout><div className="py-20 text-center">Loading...</div></AppLayout>;

  if (!canSubmitExpenses) {
    return (
      <AppLayout>
        <PageBackLink to={`/outstation-travel/${id}`} />
        <div className="py-20 text-center text-[var(--color-text-secondary)]">
          Expenses can be submitted only by the employee who raised the request after it is approved.
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageBackLink to={`/outstation-travel/${id}`} />

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-[28px] font-bold text-foreground leading-tight">Submit Expenses</h1>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">For request {request.requestRef} • {request.placeOfTravel}</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">Total Amount</div>
          <div className="text-2xl font-bold text-[var(--color-primary)]">
            {request.currencySymbol || '₹'} {expenseLines.reduce((sum, line) => sum + (line.amount || 0), 0).toLocaleString()}
          </div>
        </div>
      </div>

      <div className="max-w-5xl">
        <div className="mb-6">
          {expenseLines.map((line, index) => (
            <ExpenseLineRow
              key={index}
              line={line}
              expenseHeads={eligibleHeads}
              onChange={(newLine) => updateLine(index, newLine)}
              onRemove={() => removeLine(index)}
            />
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mt-8 pt-6 border-t border-[var(--color-border)]">
          <button
            onClick={addLine}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[var(--color-primary)] text-[var(--color-primary)] font-bold text-[12px] uppercase tracking-wider hover:bg-[var(--color-primary)]/5 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Expense Line
          </button>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/outstation-travel/${id}`)}
              className="h-11 px-6 rounded-xl border-[var(--color-text-border)]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || expenseLines.length === 0}
              className="h-11 px-10 rounded-xl bg-[var(--color-primary)] text-white font-bold shadow-lg hover:bg-[var(--color-accent)]"
            >
              {submitting ? 'Submitting...' : 'Submit All Expenses'}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};